/**
 * KnowledgeLabPanel Component
 * Main container for Knowledge Lab tab - coordinates document management and search
 */

import React from 'react';
import { DocumentUploader } from './DocumentUploader';
import { DocumentList } from './DocumentList';
import { DocumentViewer } from './DocumentViewer';
import { KnowledgeStats } from './KnowledgeStats';
import { useKnowledgeManagement } from '../../hooks/useKnowledgeManagement';
import type { KnowledgeDocument } from '../../types';

interface KnowledgeLabPanelProps {
  assistantId?: string;
  onDocumentsChange?: (count: number) => void;
  className?: string;
}

export const KnowledgeLabPanel: React.FC<KnowledgeLabPanelProps> = ({
  assistantId,
  onDocumentsChange,
  className = ''
}) => {
  const {
    documents,
    selectedDocument,
    searchResults,
    isUploading,
    isSearching,
    uploadDocument,
    deleteDocument,
    selectDocument,
    searchKnowledge,
    loadDocuments,
    getKnowledgeStats
  } = useKnowledgeManagement();

  const [searchQuery, setSearchQuery] = React.useState('');
  const [stats, setStats] = React.useState<any>(null);

  // Load documents on mount
  React.useEffect(() => {
    if (assistantId) {
      loadDocuments(assistantId);
      loadStats();
    }
  }, [assistantId, loadDocuments]);

  // Load stats
  const loadStats = async () => {
    if (!assistantId) return;
    try {
      const statsData = await getKnowledgeStats(assistantId);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Notify parent of document count changes
  React.useEffect(() => {
    if (onDocumentsChange) {
      onDocumentsChange(documents.length);
    }
  }, [documents.length, onDocumentsChange]);

  const handleUpload = async (file: File) => {
    const success = await uploadDocument(file);
    if (success && assistantId) {
      await loadDocuments(assistantId);
      await loadStats();
    }
  };

  const handleDelete = async (documentId: string) => {
    const success = await deleteDocument(documentId);
    if (success && assistantId) {
      await loadDocuments(assistantId);
      await loadStats();
      if (selectedDocument?.id === documentId) {
        selectDocument(null);
      }
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      await searchKnowledge(query, 10);
    }
  };

  return (
    <div className={`flex-1 flex flex-col gap-6 p-6 ${className}`}>
      {/* Stats Overview */}
      <KnowledgeStats
        stats={stats}
        documentCount={documents.length}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
        {/* Left Column: Upload & List */}
        <div className="lg:col-span-1 space-y-6">
          <DocumentUploader
            onUpload={handleUpload}
            isUploading={isUploading}
            disabled={!assistantId}
          />

          <DocumentList
            documents={documents}
            selectedDocument={selectedDocument}
            onSelect={selectDocument}
            onDelete={handleDelete}
            onSearch={handleSearch}
            searchQuery={searchQuery}
            searchResults={searchResults}
            isSearching={isSearching}
          />
        </div>

        {/* Right Column: Document Viewer */}
        <div className="lg:col-span-2">
          <DocumentViewer
            document={selectedDocument}
            searchResults={searchResults}
          />
        </div>
      </div>
    </div>
  );
};

export default KnowledgeLabPanel;
